/**
 * pixi-particles — Interpolation.js
 *
 * Functions of the form `(array, t) => number`, used by EmitterOp/EmitterColorOp
 * when a property is configured with `{ values: [...], interpolation: 'catmull' }`.
 */

function Linear (v: number[], t: number): number
{
    var m = v.length - 1;
    var f = m * t;
    var i = Math.floor(f);

    if (t < 0) { return LinearHelper(v[0], v[1], f); }
    if (t > 1) { return LinearHelper(v[m], v[m - 1], m - f); }

    return LinearHelper(v[i], v[(i + 1 > m) ? m : i + 1], f - i);
}

function LinearHelper (p0: number, p1: number, t: number): number
{
    return (p1 - p0) * t + p0;
}

function CatmullRomHelper (t: number, p0: number, p1: number, p2: number, p3: number): number
{
    var v0 = (p2 - p0) * 0.5;
    var v1 = (p3 - p1) * 0.5;
    var t2 = t * t;
    var t3 = t * t2;

    return (2 * p1 - 2 * p2 + v0 + v1) * t3 + (-3 * p1 + 3 * p2 - 2 * v0 - v1) * t2 + v0 * t + p1;
}

function CatmullRom (v: number[], t: number): number
{
    var m = v.length - 1;
    var f = m * t;
    var i = Math.floor(f);

    if (v[0] === v[m])
    {
        if (t < 0) { i = Math.floor(f = m * (1 + t)); }

        return CatmullRomHelper(f - i, v[(i - 1 + m) % m], v[i], v[(i + 1) % m], v[(i + 2) % m]);
    }
    else
    {
        if (t < 0) { return v[0] - (CatmullRomHelper(-f, v[0], v[0], v[1], v[1]) - v[0]); }
        if (t > 1) { return v[m] - (CatmullRomHelper(f - m, v[m], v[m], v[m - 1], v[m - 1]) - v[m]); }

        return CatmullRomHelper(f - i, v[i ? i - 1 : 0], v[i], v[m < i + 1 ? m : i + 1], v[m < i + 2 ? m : i + 2]);
    }
}

function Bernstein (n: number, i: number): number
{
    function Factorial (x: number): number
    {
        var s = 1;

        for (var a = x; a > 1; a--) { s *= a; }

        return s;
    }

    return Factorial(n) / Factorial(i) / Factorial(n - i);
}

function BezierHelper (t: number, n: number, points: number[]): number
{
    var b = 0;
    var pow = Math.pow;

    for (var i = 0; i < n; i++)
    {
        b += pow(1 - t, n - i) * pow(t, i) * points[i] * Bernstein(n, i);
    }

    return b;
}

function Bezier (v: number[], t: number): number
{
    var n = v.length - 1;

    return BezierHelper(t, n, v);
}

export type InterpolationFn = (v: number[], t: number) => number;

export var Interpolation: Record<string, InterpolationFn> = {
    linear: Linear,
    catmull: CatmullRom,
    catmullrom: CatmullRom,
    bezier: Bezier
};

export function GetInterpolationFunction (name?: string | InterpolationFn): InterpolationFn
{
    if (typeof name === 'function')
    {
        return name;
    }

    return (name && Interpolation[name]) || Interpolation.linear;
}
